<template>
	<div class="erco-text-input">
		<label class="label_" :droppable="`${droppable}`" @click.capture="handleLabelClick($event)">
			<span class="_text text-muted">{{ label || '&nbsp;' }} <sup v-if="showMandatory">*</sup></span>
			<erco-tooltip v-if="tooltip && tooltip.text"
			              :variant="tooltip.variant"
			              :title="tooltip.title"
			              :text="tooltip.text">
			</erco-tooltip>
			<div class="row">
				<div class="_input-col col-12">
					<b-input-group>
						<b-form-input v-model="textvalue"
						              ref="input"
						              :class="['_input', { '_focus-outline': forceFocusOutline, '_error': error }]"
						              :style="inputStyle"
						              :textarea="isLongOrComplexText"
						              :placeholder="placeholder"
						              :disabled="disabled"
						              @focus="handleFocus"
						              @blur="handleBlur"
						              @change="handleChange"
						              @input="handleInput">
						</b-form-input>

						<b-input-group-button v-if="this.isResetable">
							<b-button class="_reset"
							      variant="danger"
							      @click="handleReset($event)">
								&times;
							</b-button>
						</b-input-group-button>
					</b-input-group>
				</div>
			</div>
		</label>
	</div>
</template>
<script>

import ErcoTooltip from '@/components/molecules/ErcoTooltip';

export default {
	name: 'ErcoTextInputMolecule',
	props: {
		label: {
			type: String,
			default: '',
		},
		text: {
			type: String,
			default: '',
		},
		placeholder: {
			type: String,
			default: '',
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		keepUnfocused: {
			type: Boolean,
			default: false,
		},
		resetable: {
			type: Boolean,
			default: false,
		},
		droppable: {
			type: Boolean,
			default: false,
		},
		forcePointerCursor: {
			type: Boolean,
			default: false,
		},
		forceFocusOutline: {
			type: Boolean,
			default: false,
		},
		inputType: {
			type: String,
			default: 'input',
			validator: value => (['input', 'textarea', 'auto'].indexOf(value.toLowerCase()) > -1),
		},
		charlength: {
			type: [Number, Boolean],
			default: false,
		},
		showMandatory: {
			type: Boolean,
			default: false,
		},
		error: {
			type: Boolean,
			default: false,
		},
		tooltip: {
			type: Object,
			required: false,
			default: () => ({ variant: '', title: '', text: '' }),
		},
	},

	components: {
		ErcoTooltip,
	},

	data() {
		return {
			dataText: null,
			hadFocus: false,
		};
	},

	mounted() {
		this.handleInputHeight();
	},

	methods: {
		handleFocus() {
			if (this.keepUnfocused) {
				this.$refs.input.$el.blur();
				return;
			}

			this.hadFocus = true;
			this.$emit('focus');
		},

		handleBlur() {
			if (this.keepUnfocused) {
				return;
			}

			this.hadFocus = false;
		},

		handleChange(value) {
			const valueAfterCheck = this.checkCharlengthRestriction(value);
			this.$emit('change', valueAfterCheck);
		},

		handleInput(value) {
			const valueAfterCheck = this.checkCharlengthRestriction(value);
			this.$emit('input', valueAfterCheck);
			this.handleInputHeight();
		},

		handleReset($event) {
			this.dataText = '';
			this.$emit('reset', $event);
		},

		handleInputHeight() {
			/* Nur Textarea berücksichtigen */
			if (this.isLongOrComplexText) {
				const el = this.$refs.input.$el;
				el.style.height = 'auto';
				el.style.overflowY = 'hidden';
				el.style.height = `${el.scrollHeight}px`;
			}
		},

		handleLabelClick($event) {
			if ($event.target.classList.contains('_reset')) {
				$event.preventDefault();
				return;
			}
			this.$emit('click', $event);
		},

		resetInput() {
			this.dataText = '';
		},

		checkCharlengthRestriction(value) {
			let workingValue = value;

			if (this.charlength !== false &&
				this.charlength < workingValue.length) {
				workingValue = workingValue.slice(0, this.charlength);
				this.textvalue = workingValue;
				this.$refs.input.$el.value = workingValue;
			}

			return workingValue;
		},

		focus() {
			this.$refs.input.focus();
		},
	},

	computed: {
		isLongOrComplexText() {
			const charCountThreshold = 50;
			const val = this.textvalue;
			const hasLineBreaks = !!val.match(/<\s*br\s*\/{0,1}\s*>/) || !!val.match(/\n/gm);

			switch (this.inputType.toLowerCase()) {
				case 'input':
					return false;
				case 'textarea':
					return true;
				default:
					return (this.textvalue.length > charCountThreshold) || hasLineBreaks;
			}
		},

		inputStyle() {
			let style = '';

			if (this.keepUnfocused) {
				style = 'cursor: default;';
			}
			if (this.forcePointerCursor) {
				style = 'cursor: pointer;';
			}

			return style;
		},

		textvalue: {
			get() {
				return (typeof this.dataText === 'string') ? this.dataText : this.text;
			},
			set(value) {
				this.dataText = value;
			},
		},

		isResetable() {
			return this.resetable && this.textvalue;
		},
	},

	watch: {
		text(newText) {
			this.dataText = newText;
		},

		isLongOrComplexText() {
			this.focus();
			this.handleInputHeight();
		},
	},

};
</script>

<style lang="scss">

	.erco-text-input {
		.label_ {
			width: 100%;
			margin-bottom: 1.5rem;

			& > .row {
				& > ._reset-col {
					padding-left: 0;
					margin-left: -0.3em;
				}
			}

			& > ._text {
				display: inline-block;
				margin-bottom: 0.5rem;
			}
		}

		._input {
			&::placeholder {
				color: rgba(0, 0, 0, 0.2);
			}

			&._focus-outline {
				border-color: #5cb3fd !important;
				outline: none;
			}

			&._error {
				border: 1px solid red;
			}
		}
	}

</style>



// WEBPACK FOOTER //
// ErcoTextInput.vue?69dd483d